import { useEffect, useState, type FormEvent } from 'react';
import { trackPageView, Analytics } from '../utils/analytics';
import { sendContactForm, type ContactFormData } from '../api/contact';
import useSmoothScroll from '../hooks/useSmoothScroll';

// ===== NAV =====

function Nav24h() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav className={`nav${scrolled ? ' scrolled' : ''}`}>
      <div className="nav-inner">
        <a href="/" className="nav-logo">
          <img src="/icon.png" alt="SyncroX" className="nav-logo-img" />
          <img src="/logo_light2.png" alt="SyncroX" className="nav-logo-wordmark" />
        </a>
        <ul className="nav-links">
          <li><a href="#problem">Proč to řešit</a></li>
          <li><a href="#how">Jak to funguje</a></li>
          <li><a href="#offer">Co získáte</a></li>
          <li><a href="#cta" className="nav-cta">Domluvit konzultaci &rarr;</a></li>
        </ul>
      </div>
    </nav>
  );
}

// ===== CONTACT FORM =====

function ConsultationForm() {
  const [form, setForm] = useState<ContactFormData>({
    name: '', email: '', phone: '', company: '', subject: '24h-kontrola', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await sendContactForm({
        ...form,
        message: form.message || 'Mám zájem o 24h kontrolu firmy — chci konzultaci.',
      });
      if (res.success) {
        setStatus({ type: 'success', msg: 'Děkujeme. Ozveme se vám do 24 hodin.' });
        setForm({ name: '', email: '', phone: '', company: '', subject: '24h-kontrola', message: '' });
        Analytics.contactFormSubmit('24h-kontrola');
      } else {
        setStatus({ type: 'error', msg: res.message || 'Něco se pokazilo.' });
      }
    } catch {
      setStatus({ type: 'error', msg: 'Chyba připojení. Zavolejte nám na +420 773 751 263.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="contact-form-card" onSubmit={handleSubmit}>
      <h3>Domluvit 15min konzultaci</h3>
      <div className="form-row">
        <div className="form-group">
          <label>Jméno *</label>
          <input className="form-input" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jan Novák" />
        </div>
        <div className="form-group">
          <label>E-mail *</label>
          <input className="form-input" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jan@firma.cz" />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Telefon</label>
          <input className="form-input" type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+420..." />
        </div>
        <div className="form-group">
          <label>Firma</label>
          <input className="form-input" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Firma s.r.o." />
        </div>
      </div>
      <div className="form-group">
        <label>Co vás nejvíc trápí?</label>
        <textarea className="form-textarea" rows={3} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Např. nemám přehled o marži, rozhoduji se pozdě, data mám jen v Excelu..." />
      </div>
      <button type="submit" className="form-submit" disabled={submitting}>
        {submitting ? 'Odesílám...' : 'Chci konzultaci zdarma'}
      </button>
      {status && <div className={`form-status ${status.type}`}>{status.msg}</div>}
      <p style={{ fontSize: 12, color: '#5A5A72', marginTop: 10, textAlign: 'center' }}>15 minut. Bez závazku. Řekneme vám, jestli vám to dává smysl.</p>
    </form>
  );
}

// ===== MAIN PAGE =====

export default function ReportsLanding() {
  useSmoothScroll();

  useEffect(() => {
    trackPageView('/manazerske-reporty', 'SyncroX — 24h kontrola firmy');
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Nav24h />

      {/* ========================================
          1. HERO
          Cíl: Trefit problém, slíbit výsledek.
          ======================================== */}
      <section className="hero" style={{ paddingBottom: 80 }}>
        <div className="container">
          <div className="hero-layout">
            <div className="hero-text">
              <div className="hero-badge">
                <span className="badge-dot" />
                <span>Kapacita: <strong>max. 15 klientů měsíčně</strong></span>
              </div>

              <h1>Přestaňte řídit firmu <span className="accent">naslepo</span></h1>

              <p className="hero-sub">
                Do 24 hodin uvidíte, kde vaše firma vydělává a kde ztrácí peníze.
                Bez Excelů, bez zmatku a bez čekání na účetní.
              </p>

              <div className="hero-ctas">
                <a href="#cta" className="btn-primary">
                  Domluvit 15min konzultaci
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>
                </a>
                <a href="#how" className="btn-secondary">Jak to funguje</a>
              </div>

              <div className="hero-proof">
                <div className="hero-proof-item"><div className="number">24 h</div><div className="label">od přístupů<br />po první výstup</div></div>
                <div className="hero-proof-divider" />
                <div className="hero-proof-item"><div className="number">2</div><div className="label">integrace<br />dle výběru</div></div>
                <div className="hero-proof-divider" />
                <div className="hero-proof-item"><div className="number">3</div><div className="label">konkrétní<br />rozhodnutí</div></div>
              </div>
            </div>

            {/* Hero visual — čistý přehled, ne software screenshot */}
            <div className="hero-visual">
              <div className="sync-animation">
                <div className="sync-top">
                  <div className="sync-top-left">
                    <div className="sync-top-dot" />
                    <span className="sync-top-label">Kontrola firmy — živá data</span>
                  </div>
                  <span className="sync-counter">Aktuální</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* KPI summary */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ background: 'var(--bg-section)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.04em', marginBottom: 6 }}>Kde vyděláváte</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--green)' }}>Shoptet</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>marže 26 % · tržby ↑ 12 %</div>
                    </div>
                    <div style={{ background: 'var(--bg-section)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.04em', marginBottom: 6 }}>Kde ztrácíte</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--red)' }}>Etsy</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>marže 4 % · poplatky 18 %</div>
                    </div>
                    <div style={{ background: 'var(--bg-section)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.04em', marginBottom: 6 }}>Cash flow</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--accent)' }}>+340K</div>
                      <div style={{ fontSize: 12, color: 'var(--green)', marginTop: 4 }}>↑ pozitivní trend</div>
                    </div>
                    <div style={{ background: 'var(--bg-section)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.04em', marginBottom: 6 }}>Co řešit první</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-heading)', lineHeight: 1.35, marginTop: 4 }}>Vypnout Etsy kampaň.<br />Přesunout budget na Shoptet.</div>
                    </div>
                  </div>
                  {/* Mini chart */}
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 44 }}>
                    {[35,45,30,55,40,60,50,70,65,80,75,90,85,100,92,88].map((h, i) => (
                      <div key={i} style={{ flex: 1, height: h + '%', background: `rgba(124,58,237,${0.15 + (h / 100) * 0.5})`, borderRadius: 2 }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', textAlign: 'center' }}>Reálná čísla. Reálná rozhodnutí.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          2. PROBLÉM
          Cíl: Pojmenovat realitu klienta.
          ======================================== */}
      <section className="problem-section" id="problem">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Poznejte se?</span>
            <h2>Takhle dnes vypadá řízení většiny firem</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 48 }}>
            {[
              { icon: '&#128203;', title: 'Všechno je v Excelu', desc: 'Tabulky na tržby, tabulky na náklady, tabulky na objednávky. A žádná z nich nesedí s tou druhou.' },
              { icon: '&#9203;', title: 'Data dostáváte pozdě', desc: 'Účetní pošle výkaz za minulý měsíc. Ale vy potřebujete vědět, co se děje TEĎ.' },
              { icon: '&#128187;', title: 'Musíte být u PC', desc: 'Abyste měli přehled, musíte otevřít 4 systémy, porovnat čísla a dopočítat ručně.' },
              { icon: '&#129300;', title: 'Rozhodujete pocitově', desc: '„Asi se nám daří." „Snad to vyjde." Nemáte tvrdá čísla, takže rozhodujete naslepo.' },
              { icon: '&#128176;', title: 'Nevíte, kde ztrácíte', desc: 'Který kanál je ztrátový? Který produkt vás stojí víc, než vydělá? Nevíte — protože nemáte data.' },
              { icon: '&#128104;&#8205;&#128187;', title: 'Účetní ≠ manažerský přehled', desc: 'Účetní dělá účetnictví. Ne manažerské řízení. To, co potřebujete, v účetních výkazech nenajdete.' },
            ].map((card, i) => (
              <div key={i} className="cost-card" style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 28, marginBottom: 12 }} dangerouslySetInnerHTML={{ __html: card.icon }} />
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-heading)', marginBottom: 8 }}>{card.title}</div>
                <div className="cost-desc">{card.desc}</div>
              </div>
            ))}
          </div>

          <div className="cost-total" style={{ borderColor: 'var(--accent)', boxShadow: '0 0 0 4px var(--accent-soft)' }}>
            <div className="total-label" style={{ color: 'var(--text-secondary)' }}>Výsledek?</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--text-heading)', letterSpacing: '-0.02em', marginBottom: 4 }}>Řídíte firmu naslepo.</div>
            <div className="total-note">A každé pozdní nebo špatné rozhodnutí vás stojí peníze, které nikdy neuvidíte.</div>
          </div>
        </div>
      </section>

      {/* ========================================
          3. TRANSFORMACE — Před / Po
          ======================================== */}
      <section style={{ padding: '80px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="section-header">
            <h2>Co se změní</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 1fr', gap: 0, alignItems: 'stretch', maxWidth: 800, margin: '0 auto' }}>
            {/* BEFORE */}
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '32px 28px' }}>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'var(--red)', marginBottom: 20 }}>Dnes</div>
              {['Excel tabulky', 'Data 3–4 týdny stará', 'Rozhodování pocitově', 'Závislost na účetní', 'Musíte být u PC', 'Nevíte, co řešit první'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontSize: 14, color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--red)', fontWeight: 700, fontSize: 16 }}>&#10007;</span>{item}
                </div>
              ))}
            </div>
            {/* Arrow */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 28, color: 'var(--accent)' }}>&rarr;</div>
            </div>
            {/* AFTER */}
            <div style={{ background: 'var(--bg-card)', border: '2px solid var(--accent)', borderRadius: 'var(--radius-xl)', padding: '32px 28px', boxShadow: '0 0 0 4px var(--accent-soft)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'var(--accent)', marginBottom: 20 }}>Po 24 hodinách</div>
              {['Jasná čísla na jednom místě', 'Data aktuální ke včerejšku', 'Rozhodnutí na základě faktů', 'Nezávislost na účetní', 'Přehled z telefonu i z PC', 'Víte přesně, co řešit jako první'].map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontSize: 14, color: 'var(--text-body)' }}>
                  <span style={{ color: 'var(--green)', fontWeight: 700, fontSize: 16 }}>&#10003;</span>{item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          4. JAK TO FUNGUJE
          Cíl: Snížit vnímanou složitost.
          ======================================== */}
      <section className="how-section" id="how">
        <div className="container">
          <div className="section-header">
            <span className="section-label">3 kroky. Žádná složitost.</span>
            <h2>Jak získáte kontrolu nad firmou</h2>
            <p>Nemusíte nic instalovat. Nemusíte nic připravovat. Vše uděláme za vás.</p>
          </div>
          <div className="how-steps">
            <div className="how-step">
              <div className="step-number" style={{ background: 'var(--accent-soft)', border: '2px solid var(--accent)', color: 'var(--accent)' }}>1</div>
              <div className="step-duration" style={{ color: 'var(--accent)', background: 'var(--accent-soft)' }}>15 min</div>
              <h3 className="step-title">Dodáte přístupy</h3>
              <p className="step-desc">Na krátké konzultaci probereme, jaké systémy používáte. Pošlete nám přístupy — nic víc od vás nepotřebujeme.</p>
            </div>
            <div className="how-step">
              <div className="step-number" style={{ background: 'var(--gold-soft)', border: '2px solid var(--gold)', color: 'var(--gold)' }}>2</div>
              <div className="step-duration" style={{ color: 'var(--gold)', background: 'var(--gold-soft)' }}>Do 24h</div>
              <h3 className="step-title">Napojíme data a nastavíme přehled</h3>
              <p className="step-desc">Propojíme vaše systémy, připravíme klíčové přehledy zaměřené na to, co potřebujete vědět pro řízení firmy.</p>
            </div>
            <div className="how-step">
              <div className="step-number" style={{ background: 'var(--green-soft)', border: '2px solid var(--green)', color: 'var(--green)' }}>3</div>
              <div className="step-duration" style={{ color: 'var(--green)', background: 'var(--green-soft)' }}>Hotovo</div>
              <h3 className="step-title">Vidíte klíčová čísla a víte, co řešit</h3>
              <p className="step-desc">Otevřete přehled a máte jasno. Kde vyděláváte, kde ztrácíte, co řešit jako první. Každý den aktuální.</p>
            </div>
          </div>

          <div style={{ textAlign: 'center', marginTop: 48 }}>
            <a href="#cta" className="btn-primary">Domluvit konzultaci</a>
          </div>
        </div>
      </section>

      {/* ========================================
          5. CO ZÍSKÁTE
          Cíl: Vyjádřit hodnotu nabídky.
          ======================================== */}
      <section className="tabs-section" id="offer">
        <div className="container">
          <div className="section-header">
            <span className="section-label gold">Co získáte</span>
            <h2>24h kontrola firmy — kompletní balíček</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 48 }}>
            {[
              { icon: '&#9889;', title: '24h kontrola firmy', desc: 'Přehled klíčových čísel zaměřený na rozhodování — ne na účetnictví. Víte, kde vyděláváte a kde ztrácíte.', highlight: true },
              { icon: '&#128279;', title: '2 integrace dle výběru', desc: 'Propojíme 2 systémy, které denně používáte. Žádné ruční přepisování, žádné exporty.' },
              { icon: '&#127919;', title: 'Výstup zaměřený na rozhodování', desc: 'Nedáváme vám data. Dáváme vám odpovědi. Co řešit. Kde šetřit. Kam investovat.' },
              { icon: '&#128269;', title: 'Rychlá diagnostika firmy', desc: 'Během konzultace identifikujeme 3 největší ztráty nebo slabá místa, která vás stojí peníze.' },
              { icon: '&#128506;', title: 'Mapa ztrát a neefektivity', desc: 'Analyzujeme vaše procesy a navrhneme, co zautomatizovat, zjednodušit nebo úplně zrušit.' },
              { icon: '&#9881;', title: 'Návrh digitalizace procesů', desc: 'Řekneme vám konkrétně, jak propojit systémy a odstranit ruční práci, která vás brzdí.' },
            ].map((card, i) => (
              <div key={i} style={{
                background: card.highlight ? 'var(--accent-soft)' : 'var(--bg-card)',
                border: card.highlight ? '2px solid var(--accent)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-xl)', padding: '28px 24px',
                boxShadow: card.highlight ? 'var(--shadow-purple)' : 'var(--shadow-sm)',
              }}>
                <div style={{ fontSize: 28, marginBottom: 14 }} dangerouslySetInnerHTML={{ __html: card.icon }} />
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-heading)', marginBottom: 10 }}>{card.title}</div>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{card.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================
          6. PROČ TO NENÍ JEN REPORTING
          ======================================== */}
      <section className="reports-section">
        <div className="container">
          <div className="reports-header">
            <span className="section-label on-dark">Důležité</span>
            <h2>Tohle není další reporting.<br />Tohle je <span className="accent">kontrola nad firmou</span>.</h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24, marginBottom: 48, position: 'relative', zIndex: 1 }}>
            {[
              { no: 'Další dashboard plný čísel', yes: 'Jasný přehled, který říká CO DĚLAT' },
              { no: 'Účetní výkaz v jiném kabátě', yes: 'Manažerský pohled na firmu' },
              { no: 'Nástroj, který se musíte učit', yes: 'Výstup, kterému rozumíte za 2 minuty' },
              { no: 'Závislost na IT oddělení', yes: 'Funguje hned, nastavíme za vás' },
            ].map((item, i) => (
              <div key={i} className="report-card" style={{ display: 'flex', gap: 20, alignItems: 'flex-start', padding: '24px 28px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#F87171', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>&#10007;</span> Ne
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text-on-dark-secondary)' }}>{item.no}</div>
                </div>
                <div style={{ width: 1, background: 'var(--border-dark)', alignSelf: 'stretch' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#4ADE80', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 14 }}>&#10003;</span> Ano
                  </div>
                  <div style={{ fontSize: 14, color: 'var(--text-on-dark)' }}>{item.yes}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="reports-cta">
            <a href="#cta" className="btn-primary btn-on-dark" style={{ fontSize: 16, padding: '16px 32px' }}>Chci kontrolu nad firmou</a>
          </div>
        </div>
      </section>

      {/* ========================================
          7. GARANCE
          ======================================== */}
      <section className="guarantee-section">
        <div className="container">
          <div className="guarantee-box">
            <div className="guarantee-icon">&#128170;</div>
            <div className="guarantee-content">
              <h3>Garance prvních rozhodnutí</h3>
              <p>
                <strong>Do 14 dnů získáte minimálně 3 konkrétní rozhodnutí s reálným dopadem na vaši firmu.</strong> Kde ušetřit. Co zrušit. Kam přesunout peníze. Pokud ne, pokračujeme zdarma, dokud je nezískáte.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================
          8. KAPACITA / URGENCE
          ======================================== */}
      <section style={{ padding: '60px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{
            maxWidth: 700, margin: '0 auto', textAlign: 'center',
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-xl)', padding: '40px 36px',
            boxShadow: 'var(--shadow-md)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>&#128274;</div>
            <h3 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-heading)', marginBottom: 12 }}>Kapacita: max. 15 klientů měsíčně</h3>
            <p style={{ fontSize: 15, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 500, margin: '0 auto' }}>
              Každé firmě nastavujeme výstupy individuálně. Proto pracujeme maximálně s 15 novými klienty měsíčně. Až bude kapacita naplněná, otevíráme čekací listinu.
            </p>
          </div>
        </div>
      </section>

      {/* ========================================
          9. FAQ
          ======================================== */}
      <Faq24h />

      {/* ========================================
          10. FINÁLNÍ CTA
          ======================================== */}
      <section className="final-cta" id="cta">
        <div className="container">
          <div className="final-cta-inner">
            <div className="final-cta-text">
              <h2>Zjistěte během 15 minut,<br />jak by mohla vypadat<br /><span className="accent">kontrola vaší firmy</span>.</h2>
              <p>Řekneme vám, jestli vám to dává smysl. Bez tlaku. Bez závazku. A pokud ano — do 24 hodin uvidíte první čísla.</p>
              <a href="tel:+420773751263" className="final-cta-phone">&#128222; +420 773 751 263</a>
              <div className="final-cta-note">Odpovídáme do 24 hodin. Obvykle rychleji.</div>
            </div>
            <ConsultationForm />
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="footer">
        <div className="container">
          <div className="footer-inner">
            <div className="footer-left">
              <img src="/icon.png" alt="SyncroX" className="footer-logo-img" />
              <span className="footer-copy">&copy; 2025 SyncroX. Certifikovaný partner Money S3.</span>
            </div>
            <div className="footer-links">
              <a href="/">Hlavní stránka</a>
              <a href="#">Ochrana osobních údajů</a>
              <a href="#">Podpora</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

// ===== FAQ =====

function Faq24h() {
  const [open, setOpen] = useState(0);

  const faqs = [
    { q: 'Co když máme v datech chaos?', a: 'Právě proto to děláme. Nepotřebujeme dokonalá data — potřebujeme přístupy do vašich systémů. Zbytek vyřešíme my. Chaos v datech je důvod, proč klienti přicházejí, ne důvod proč odcházejí.' },
    { q: 'Co když nemáme nic připravené?', a: 'Nepotřebujete nic připravovat. Na konzultaci zjistíme, jaké systémy používáte, a řekneme vám, co od vás budeme potřebovat. Typicky jsou to jen přístupy — žádné exporty, žádné tabulky.' },
    { q: 'Jak rychle to opravdu spustíte?', a: 'Do 24 hodin od chvíle, kdy dostaneme přístupy. Ne od podpisu smlouvy, ne od první schůzky — od momentu, kdy máme přístupy do vašich systémů.' },
    { q: 'Musí to řešit naše IT?', a: 'Ne. Vše nastavujeme na naší straně. Nepotřebujete IT oddělení, programátora ani administrátora. Stačí, když nám pošlete přístupové údaje.' },
    { q: 'Používáme účetní systém a další nástroje. Dá se to propojit?', a: 'Propojujeme přes 25 systémů — účetní programy (Money S3, Pohoda), e-shopy (Shoptet, WooCommerce), platební brány (Stripe, PayPal, Comgate) a další. Pokud váš systém v seznamu není, většinu nových integrací vytvoříme do 2 týdnů.' },
    { q: 'Je to pro nás vhodné, i když nemáme velkou firmu?', a: 'Ano. Pracujeme s firmami od 2 do 200 zaměstnanců. Čím menší firma, tím víc záleží na každém rozhodnutí — a tím víc vám přehled nad čísly pomůže.' },
  ];

  return (
    <section className="faq-section">
      <div className="container">
        <div className="section-header"><h2>Časté otázky</h2></div>
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <div key={i} className={`faq-item${open === i ? ' open' : ''}`}>
              <button className="faq-question" onClick={() => setOpen(open === i ? -1 : i)}>
                {faq.q}<span className="faq-toggle">+</span>
              </button>
              <div className="faq-answer">{faq.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
